import { useRouter } from '@abyss/web/hooks/useRouter';
import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  CoverageTypesCodes,
  ReverseCoverageTypesCodes,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { ContentWrapper } from '../../../../../common/ContentWrapper';
import { ImageComponent } from '../../../../../common/ImageComponent';
import {
  createPostProviderPayload,
  getGuestPlanSelectionData,
  triggerResponseDataUpdated,
} from '../../../../../frontends/GuestExperience/Guest.utils';
import { useDeleteGuestAcquisition } from '../../../../../hooks/useDeleteGuestAcquisition';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { usePostProviderInfo } from '../../../../../hooks/usePostProviderInfo';
import { ProviderLocation } from '../../../../../models/ProviderDetails';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useGuestAcquisitionStore } from '../../../../../store/useGuestAcquisitionStore';
import {
  GuestAcquisitionState,
  ProviderDetailsMaestroResponse,
} from '../../../../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import {
  getFormattedPhoneNumber,
  getPrimaryPhone,
} from '../../../../../utils/phone.utils';
import { shortenZipCode } from '../../../../../utils/providerDetails.utils';
import {
  capitalizeFirstLetters,
  getNameAttrs,
  parseProviderName,
} from '../../../../../utils/providerNames.utils';
import {
  getCurrentMember,
  getNetworkIdsForPES,
} from '../../../../../utils/user.utils';
import { SaveAndCancelButton } from '../ChoosePCPLocation/ChoosePCPLocation.styled';
import { StyledConfirmPCPAddressCardText } from '../ChoosePCPLocation/ConfirmPCPLocation.styled';
import {
  ChooseLocationCardsSuccessContainer,
  ChoosePCPPhoto,
  ClinicNameAddress,
  ContentContainer,
  ContentWrapperFullWidth,
  LocationWrapper,
  LocationWrapperFullWidth,
  LocationsDropdown,
  PCPHeaderContainer,
  SubSuccessProvider,
  SubSuccessProviderSpeciality,
  SuccessMessageHeading,
  SuccessPageBackButton,
  SuccessPageYourPCPHeader,
  SuccessPageYourPCPLocationHeader,
  SuccessSelectedPCPBox,
} from './ChoosePCPSuccess.styled';

export type ActionLocation = {
  provider: ProviderDetailsMaestroResponse;
  addedLocation: ProviderLocation[];
  removedLocation: ProviderLocation[];
};

type LocationSavedProps = {
  actionLocation: ActionLocation;
};

function LocationSaved({ actionLocation }: LocationSavedProps) {
  const urlParams = new URLSearchParams(window.location.search);
  const chipValueFromParams = urlParams.get('chipValue');
  const { navigate } = useRouter();
  const { t } = useTranslation();

  const { requestData, responseData, setResponseData, referringUrl } =
    useGuestAcquisitionStore(
      useShallow((state: GuestAcquisitionState) => ({
        requestData: state.requestData,
        responseData: state.responseData,
        setResponseData: state.setResponseData,
        referringUrl: state.referringUrl,
      }))
    );

  const [, deleteGuestAcquisition] = useDeleteGuestAcquisition({});
  const [, postProviderInfo] = usePostProviderInfo({});

  const { selectedPlan } = getGuestPlanSelectionData();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType } = chipStore;

  const featureFlags = useLagoon('feature-flags')();
  const currentMember = getCurrentMember();
  const {
    provider: maestroSelected,
    addedLocation,
    removedLocation,
  } = actionLocation;
  const {
    providerName = '',
    professionalDesignation = '',
    specialties = [],
    primaryDegrees = [],
    imageLocation = '',
    providerType,
  } = maestroSelected || {};
  const nameAttrs = getNameAttrs(maestroSelected);
  const formattedProviderName = parseProviderName(
    providerName,
    providerType,
    professionalDesignation,
    nameAttrs
  );
  const fullProviderName = primaryDegrees?.length
    ? `${formattedProviderName}, ${primaryDegrees[0]}`
    : formattedProviderName;
  const primarySpeciality = specialties[0] || '';

  const choosePCPSuccess: string = 'choose pcp success';
  const imgRef = useRef<HTMLImageElement>(null);

  const [viewMapEnhancementsFlag, enableHcpMaestro] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HCP_MAESTRO,
  ]);

  const ContentWrapperComponent = viewMapEnhancementsFlag
    ? ContentWrapperFullWidth
    : ContentWrapper;

  const LocationWrapperComponent = viewMapEnhancementsFlag
    ? LocationWrapperFullWidth
    : ContentWrapper;
  const handleBackToFindCare = () => {
    adobeLinkTrackEvent({
      name: 'back to find care',
      location: `body:${choosePCPSuccess}`,
      type: 'internal',
    });

    const selectedCoverageType =
      find(Constants.MOCK_CARE_CATEGORIES, {
        value: Constants.CHIPS_CATEGORIES.PRIMARY_CARE,
      })?.coverageType || CoverageTypesCodes.MEDICAL;

    navigate(
      referringUrl ||
        `/?chipValue=${chipValueFromParams}&coverageType=${ReverseCoverageTypesCodes[selectedCoverageType]}`
    );
  };

  const handleFinishAndReturn = async () => {
    const reciprocityId = getNetworkIdsForPES(
      currentMember,
      coverageType,
      featureFlags
    );
    const payload = createPostProviderPayload(
      requestData,
      responseData,
      selectedPlan,
      coverageType,
      reciprocityId
    );

    const result = await postProviderInfo({
      variables: { ...payload, enableHcpPsx: enableHcpMaestro },
    });

    const html = result?.data?.postProviderInfo;
    if (html) {
      setResponseData([]);
      triggerResponseDataUpdated([]);
      const transactionId = requestData?.transactionId;
      if (transactionId) {
        await deleteGuestAcquisition({
          variables: { transactionId },
        });
      }
      document.open();
      document.write(html);
      document.close();
    }
  };

  return (
    <>
      <PCPHeaderContainer data-auto-testid="choose-pcp-success-container">
        <ContentWrapperComponent>
          <SuccessSelectedPCPBox color="#FBFCFE">
            <SuccessMessageHeading
              data-auto-testid="success-message-heading"
              data-testid="success-message-heading"
              tabIndex={-1}
            >
              <h1
                aria-label={`${t(
                  'PCP_LOCATION.SUCCESS_LOCATION_SAVED'
                )} ${'for'} ${fullProviderName}`}
              >
                {t('PCP_LOCATION.SUCCESS_LOCATION_SAVED')}
              </h1>
            </SuccessMessageHeading>
          </SuccessSelectedPCPBox>
        </ContentWrapperComponent>
      </PCPHeaderContainer>
      <LocationWrapperComponent>
        <Box
          color="white"
          css={{ position: 'relative' }}
          height="unset"
          padding="10px 0px"
        >
          <ChooseLocationCardsSuccessContainer cssProps={{ isListSaved: true }}>
            <SuccessPageYourPCPHeader>
              <Heading
                css={{
                  'abyss-heading-root': {
                    fontSize: '$mediumText !important',
                    marginBottom: '12px',
                    lineHeight: '32px !important',
                  },
                }}
                display="h6"
                offset={1}
              >
                <IconMaterial
                  color="$success1"
                  css={{
                    'abyss-icon': {
                      marginRight: '6.5px',
                    },
                  }}
                  icon="check"
                  size="15px"
                />
                {t('PCP')}
              </Heading>
            </SuccessPageYourPCPHeader>
            <LocationsDropdown>
              <Flex alignItems="center">
                <ChoosePCPPhoto>
                  {ImageComponent({
                    imgRef,
                    providerType,
                    imgSrc: imageLocation,
                    sizeToken: '$choosePCPSuccessIconSize',
                  })}
                </ChoosePCPPhoto>
                <Text
                  css={{
                    lineHeight: '24px',
                    'abyss-text-root': {
                      marginLeft: '8px',
                      marginTop: '10px',
                      '@screen < $md': {
                        fontSize: '14.22px',
                      },
                    },
                  }}
                >
                  <SubSuccessProvider>{fullProviderName}</SubSuccessProvider>
                  <SubSuccessProviderSpeciality>
                    {primarySpeciality}
                  </SubSuccessProviderSpeciality>
                </Text>
              </Flex>
            </LocationsDropdown>
            {addedLocation.length > 0 && (
              <>
                <SuccessPageYourPCPLocationHeader>
                  <Flex direction="row">
                    <Heading
                      css={{
                        'abyss-heading-root': {
                          fontSize: '$mediumText !important',
                          marginBottom: '8px',
                          lineHeight: '32px !important',
                        },
                      }}
                      display="h6"
                      offset={1}
                    >
                      <IconMaterial
                        color="$success1"
                        css={{
                          'abyss-icon': {
                            marginRight: '6.5px',
                          },
                        }}
                        icon="check"
                        size="15px"
                      />
                      {t('PCP_LOCATION.ADDED')}
                    </Heading>
                  </Flex>
                </SuccessPageYourPCPLocationHeader>
                <LocationWrapper>
                  {addedLocation?.map((location, index) => {
                    const {
                      line = '',
                      city = '',
                      state = '',
                      postalCode = '',
                    } = location?.address || {};

                    const addressString = [
                      line,
                      city,
                      state && `${state} ${shortenZipCode(postalCode)}`,
                    ]
                      .filter((part) => part)
                      .join(', ');

                    const { phones, providerGroupsAffiliations } = location;
                    const primaryPhoneNumber = getPrimaryPhone(
                      phones?.phone,
                      phones?.appointment
                    );
                    return (
                      <ClinicNameAddress key={index}>
                        <Flex alignItems="center" justify="space-between">
                          <ContentContainer>
                            <Layout.Stack alignItems="left" grow space="4">
                              {maestroSelected?.networkAffiliationData
                                ?.networkAffiliationName && (
                                <>
                                  <Text
                                    data-testid="pcp-confirmation-provider-group"
                                    fontWeight="$bold"
                                    size="md"
                                  >
                                    <StyledConfirmPCPAddressCardText>
                                      {t(
                                        'NETWORK_AFFILIATION.NETWORK_AFFILIATION'
                                      )}
                                    </StyledConfirmPCPAddressCardText>
                                  </Text>
                                  <Text size="md">
                                    <StyledConfirmPCPAddressCardText>
                                      {
                                        maestroSelected?.networkAffiliationData
                                          ?.networkAffiliationName
                                      }
                                    </StyledConfirmPCPAddressCardText>
                                  </Text>
                                </>
                              )}
                              <Text
                                data-testid="pcp-confirmation-provider-group"
                                fontWeight="$bold"
                                size="md"
                              >
                                <StyledConfirmPCPAddressCardText>
                                  {capitalizeFirstLetters(
                                    providerGroupsAffiliations?.[0]?.value
                                  )}
                                </StyledConfirmPCPAddressCardText>
                              </Text>
                              <Text size="md">
                                <StyledConfirmPCPAddressCardText>
                                  {addressString}
                                </StyledConfirmPCPAddressCardText>
                              </Text>
                              <Link
                                before={<IconMaterial icon="phone" />}
                                data-testid="pcp-confirmation-phone-number"
                                href={`tel:+1-${primaryPhoneNumber}`}
                                size="md"
                              >
                                {getFormattedPhoneNumber(primaryPhoneNumber)}
                              </Link>
                            </Layout.Stack>
                          </ContentContainer>
                        </Flex>
                      </ClinicNameAddress>
                    );
                  })}
                </LocationWrapper>
              </>
            )}
            {removedLocation.length > 0 && (
              <>
                <SuccessPageYourPCPLocationHeader>
                  <Flex direction="row">
                    <Heading
                      css={{
                        'abyss-heading-root': {
                          fontSize: '$mediumText !important',
                          marginBottom: '8px',
                          lineHeight: '32px !important',
                        },
                      }}
                      display="h6"
                      offset={1}
                    >
                      <IconMaterial
                        color="$success1"
                        css={{
                          'abyss-icon': {
                            marginRight: '6.5px',
                          },
                        }}
                        icon="check"
                        size="15px"
                      />
                      {t('PCP_LOCATION.REMOVED')}
                    </Heading>
                  </Flex>
                </SuccessPageYourPCPLocationHeader>
                <LocationWrapper>
                  {removedLocation?.map((location, index) => {
                    const {
                      line = '',
                      city = '',
                      state = '',
                      postalCode = '',
                    } = location?.address || {};

                    const addressString = [
                      line,
                      city,
                      state && `${state} ${shortenZipCode(postalCode)}`,
                    ]
                      .filter((part) => part)
                      .join(', ');

                    const { phones, providerGroupsAffiliations } = location;
                    const primaryPhoneNumber = getPrimaryPhone(
                      phones?.phone,
                      phones?.appointment
                    );
                    return (
                      <ClinicNameAddress key={index}>
                        <Flex alignItems="center" justify="space-between">
                          <ContentContainer>
                            <Layout.Stack alignItems="left" grow space="4">
                              {maestroSelected?.networkAffiliationData
                                ?.networkAffiliationName && (
                                <>
                                  <Text
                                    data-testid="pcp-confirmation-provider-group"
                                    fontWeight="$bold"
                                    size="md"
                                  >
                                    <StyledConfirmPCPAddressCardText>
                                      {t(
                                        'NETWORK_AFFILIATION.NETWORK_AFFILIATION'
                                      )}
                                    </StyledConfirmPCPAddressCardText>
                                  </Text>
                                  <Text size="md">
                                    <StyledConfirmPCPAddressCardText>
                                      {
                                        maestroSelected?.networkAffiliationData
                                          ?.networkAffiliationName
                                      }
                                    </StyledConfirmPCPAddressCardText>
                                  </Text>
                                </>
                              )}
                              <Text
                                data-testid="pcp-confirmation-provider-group"
                                fontWeight="$bold"
                                size="md"
                              >
                                <StyledConfirmPCPAddressCardText>
                                  {capitalizeFirstLetters(
                                    providerGroupsAffiliations?.[0]?.value
                                  )}
                                </StyledConfirmPCPAddressCardText>
                              </Text>
                              <Text size="md">
                                <StyledConfirmPCPAddressCardText>
                                  {addressString}
                                </StyledConfirmPCPAddressCardText>
                              </Text>
                              <Link
                                before={<IconMaterial icon="phone" />}
                                data-testid="pcp-confirmation-phone-number"
                                href={`tel:+1-${primaryPhoneNumber}`}
                                size="md"
                              >
                                {getFormattedPhoneNumber(primaryPhoneNumber)}
                              </Link>
                            </Layout.Stack>
                          </ContentContainer>
                        </Flex>
                      </ClinicNameAddress>
                    );
                  })}
                </LocationWrapper>
              </>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: '31px',
                gap: '10px',
              }}
            >
              <SaveAndCancelButton
                className="cancel-button"
                data-auto-testid="choose-pcp-cancel"
                data-testid="choose-pcp-cancel"
                onClick={handleBackToFindCare}
                variant="outline"
              >
                {t('CONTINUE_SEARCHING')}
              </SaveAndCancelButton>
              <SuccessPageBackButton
                after={
                  <IconMaterial
                    color="$white"
                    css={{ margin: '15px 0 15px -10px' }}
                    icon="chevron_right"
                    size="24px"
                  />
                }
                aria-describedby="reverify"
                data-auto-testid="choose-pcp-back-home"
                data-testid="choose-pcp-back-home"
                disabled={responseData.length <= 0}
                onClick={handleFinishAndReturn}
              >
                {t('Finish_and_return')}
              </SuccessPageBackButton>
            </div>
          </ChooseLocationCardsSuccessContainer>
        </Box>
      </LocationWrapperComponent>
    </>
  );
}

export default LocationSaved;
